import React, { useState, useEffect, useRef } from 'react';
import './SearchBox.scss';
import { ISearchBoxProps } from './ISearchBoxProps';
import getSearchSuggestions from '../../../utils/searchsuggestions';
import { Log } from '../../../utils/logging';
import { Search as UdirSearchBox } from '@udir-design/react/beta';

const SearchBox: React.FC<ISearchBoxProps> & { getSearchBoxQuery: () => string } = (props) => {
    const initialTrimmed = (props.initialQuery || '').trim();
    const [value, setValue] = useState(initialTrimmed);
    const [currentQuery, setCurrentQuery] = useState(initialTrimmed);
    const [showAutocompletePanel, setShowAutocompletePanel] = useState(false);
    const [searchsuggestions, setSearchsuggestions] = useState<string[]>([]);
    const [cursor, setCursor] = useState(-1);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const inputRef = useRef<HTMLInputElement | null>(null);
    const listboxIdRef = useRef<string>('search-autocomplete-' + Math.random().toString(36).slice(2));
    const isNavigatingRef = useRef(false);

    const SUGGEST_DEBOUNCE_MS = 200;
    const SEARCH_DEBOUNCE_MS = 300;

    // Initialize on mount
    useEffect(() => {
        const trimmedInitial = (props.initialQuery || '').trim();
        if (trimmedInitial) {
            setValue(trimmedInitial);
            setCurrentQuery(trimmedInitial);
        }
        if (props.autocomplete) {
            Log("Loading autocomplete data - props.autocomplete:");
            if (trimmedInitial) {
                getAndSaveQuerySuggestions(trimmedInitial, 7);
            }
        } else {
            Log("Autocomplete is deactivated");
        }
    }, []);

    // Handle newQuery prop changes
    useEffect(() => {
        if (props.newQuery) {
            const trimmed = props.newQuery.trim();
            setValue(trimmed);
            setCurrentQuery(trimmed);
        }
    }, [props.newQuery]);

    const getAndSaveQuerySuggestions = (query: string, numhits: number) => {
        if (!props.autocomplete) return;
        if (!query || query.trim() === '') {
            setSearchsuggestions([]);
            return;
        }

        const autocompleteKey = "autocomplete-" + props.searchVertical + "-" + query;
        const storedAutocompleteJSON = sessionStorage.getItem(autocompleteKey);

        if (storedAutocompleteJSON) {
            const storedAutocomplete: string[] = JSON.parse(storedAutocompleteJSON);
            setSearchsuggestions(storedAutocomplete);
            return;
        }

        const suggestions = getSearchSuggestions(query, numhits, props.searchVertical);
        suggestions.then((result: string[]) => {
            if (result) {
                sessionStorage.setItem(autocompleteKey, JSON.stringify(result));
                setSearchsuggestions(result);
            }
        }).catch((error) => {
            Log('Error fetching search suggestions: ' + error);
        });
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setShowAutocompletePanel(false);
        setCursor(-1);
        // Trim whitespace og fjern trailing '?' (jfr. UDIRSOK-675 oppfølging:
        // bruker som søker "barnehage?" får ikke "0 treff"-melding).
        const trimmedValue = value.trim().replace(/\?+\s*$/, '').trim();
        if (trimmedValue !== value) {
            setValue(trimmedValue);
            setCurrentQuery(trimmedValue);
        }
        props.onSearch(trimmedValue, false);
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;
        setValue(newValue);
        setCurrentQuery(newValue);
        setCursor(-1);
        setShowAutocompletePanel(!!newValue);
    };

    // Debounce suggestions fetching (skip when navigating with arrow keys)
    useEffect(() => {
        if (!props.autocomplete) return;
        if (isNavigatingRef.current) {
            isNavigatingRef.current = false;
            return;
        }
        if (!value || value.trim() === '') {
            setSearchsuggestions([]);
            setShowAutocompletePanel(false);
            return;
        }
        const t = window.setTimeout(() => {
            getAndSaveQuerySuggestions(value, 7);
        }, SUGGEST_DEBOUNCE_MS);
        return () => window.clearTimeout(t);
    }, [value, props.autocomplete, props.searchVertical]);

    // Debounce search-as-you-type
    useEffect(() => {
        if (!props.searchAsYouType) return;
        if (!value || value.length < 3) return;
        const t = window.setTimeout(() => {
            props.onSearch(value, true);
        }, SEARCH_DEBOUNCE_MS);
        return () => window.clearTimeout(t);
    }, [value, props.searchAsYouType]);

    const handleClear = () => {
        setValue('');
        setCurrentQuery('');
        setSearchsuggestions([]);
        setShowAutocompletePanel(false);
        props.onSearch('', false);
    };

    const handleAutocompleteClick = (suggestion: string) => {
        Log("handleAutocompleteElementClicked");
        setValue(suggestion);
        setCurrentQuery(suggestion);
        setShowAutocompletePanel(false);
        props.onSearch(suggestion, false);
        getAndSaveQuerySuggestions(suggestion, 7);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            setShowAutocompletePanel(false);
            setCursor(-1);
            return;
        } else if (e.key === 'Escape' || e.key === 'Tab') {
            setShowAutocompletePanel(false);
            return;
        }

        // Handle arrow up and arrow down if autocomplete is activated
        if (e.key === 'ArrowDown' && cursor < searchsuggestions.length - 1) {
            e.preventDefault();
            isNavigatingRef.current = true;
            const newCursor = cursor + 1;
            setCursor(newCursor);
            setValue(searchsuggestions[newCursor]);
        } else if (e.key === 'ArrowDown' && cursor === searchsuggestions.length - 1) {
            e.preventDefault();
            isNavigatingRef.current = true;
            setCursor(-1);
            setValue(currentQuery);
        } else if (e.key === 'ArrowUp' && cursor > 0) {
            e.preventDefault();
            isNavigatingRef.current = true;
            const newCursor = cursor - 1;
            setCursor(newCursor);
            setValue(searchsuggestions[newCursor]);
        } else if (e.key === 'ArrowUp' && cursor === 0) {
            e.preventDefault();
            isNavigatingRef.current = true;
            setCursor(-1);
            setValue(currentQuery);
        } else if (e.key === 'ArrowUp' && cursor === -1) {
            e.preventDefault();
            isNavigatingRef.current = true;
            const newCursor = searchsuggestions.length - 1;
            setCursor(newCursor);
            setValue(searchsuggestions[newCursor]);
        }
    };

    const handleFocus = () => {
        if (searchsuggestions.length > 0 && currentQuery !== '') {
            setShowAutocompletePanel(true);
        }
    };

    const handleClickOutside = (e: MouseEvent) => {
        const target = e.target as Node;
        const container = containerRef.current;
        if (container && !container.contains(target)) {
            setShowAutocompletePanel(false);
        }
    };

    useEffect(() => {
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const escapeRegExp = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const highlight = (text: string, query: string) => {
        if (!query) return text;
        const parts = text.split(new RegExp(`(${escapeRegExp(query)})`, 'ig'));
        return (
            <>
                {parts.map((part, i) =>
                    part.toLowerCase() === query.toLowerCase() ? <strong key={i}>{part}</strong> : <span key={i}>{part}</span>
                )}
            </>
        );
    };

    const renderAutocomplete = () => {
        if (!props.autocomplete || !showAutocompletePanel || currentQuery === "" || searchsuggestions.length === 0) {
            return null;
        }

        return (
            <div className="autocompleteContainer">
                <ul className="autocompleteList" id={listboxIdRef.current} role="listbox">
                    {searchsuggestions.map((suggestion, idx) => {
                        const optionId = `${listboxIdRef.current}-option-${idx}`;
                        return (
                            <li
                                id={optionId}
                                role="option"
                                aria-selected={cursor === idx}
                                className={`autocompleteListElement ${cursor === idx ? 'active' : ''}`}
                                key={`autocompleteListElementGroup-${idx}`}
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => handleAutocompleteClick(suggestion)}
                            >
                                <div className="autocompleteListElementText">
                                    {highlight(suggestion, currentQuery)}
                                </div>
                            </li>
                        );
                    })}
                </ul>
            </div>
        );
    };

    const placeholderText = props.placeholderText || 'Skriv inn søkeord';

    return (
        <div className="searchBoxOuterContainer" ref={containerRef}>
            <form className="searchBoxForm" onSubmit={handleSubmit} role="search">
                <UdirSearchBox>
                    <UdirSearchBox.Input
                        aria-label='Søk'
                        aria-autocomplete="list"
                        aria-expanded={showAutocompletePanel}
                        aria-controls={showAutocompletePanel ? listboxIdRef.current : undefined}
                        aria-activedescendant={cursor >= 0 ? `${listboxIdRef.current}-option-${cursor}` : undefined}
                        placeholder={placeholderText}
                        value={value}
                        onChange={handleChange}
                        onKeyDown={handleKeyDown}
                        onFocus={handleFocus}
                        ref={inputRef as any}
                    />
                    <UdirSearchBox.Button>Søk</UdirSearchBox.Button>
                    <UdirSearchBox.Clear onClick={handleClear} />
                </UdirSearchBox>
            </form>
            {renderAutocomplete()}
        </div>
    );
};
// Static method to maintain compatibility with existing code
SearchBox.getSearchBoxQuery = () => {
    // Since we're now using controlled component, we need to get the value differently
    const input = document.querySelector('.searchBoxForm input') as HTMLInputElement;
    return input ? input.value : '';
};

export default SearchBox;
